"use client"

import {useMemo, type ReactNode} from "react";
import {DynamicIcon, iconNames, type IconName} from "lucide-react/dynamic";
import type {InsightEvidence} from "@devplaninc/codeup";
import {EvidenceSourcesPill} from "@/app/components/evidence/EvidenceSourcesPill";
import {parseEvidenceDirectiveGroupJSON} from "@/app/components/generative-markdown/evidenceDirectiveData";
import {cn} from "@/lib/utils";

type DashboardIconVariant = "dashboard" | "component" | "item";

const iconFrameClassNames: Record<DashboardIconVariant, string> = {
  dashboard: "size-11 rounded-xl border bg-primary/[0.07] text-primary shadow-sm",
  component: "size-6 rounded-md bg-primary/[0.08] text-primary",
  item: "size-8 rounded-lg bg-primary/[0.08] text-primary",
};

const iconClassNames: Record<DashboardIconVariant, string> = {
  dashboard: "size-5",
  component: "size-3.5",
  item: "size-4",
};

const dynamicIconNameSet = new Set<string>(iconNames);

export function DashboardIcon({name, variant = "component", className, fallback = null}: {
  name?: string | null;
  variant?: DashboardIconVariant;
  className?: string;
  fallback?: ReactNode;
}) {
  const iconName = dashboardDynamicIconName(name);
  if (!iconName) return fallback;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        iconFrameClassNames[variant],
        className,
      )}
      aria-hidden
    >
      <DynamicIcon name={iconName} className={iconClassNames[variant]} strokeWidth={1.8}/>
    </span>
  );
}

export function DashboardEvidence({evidence, companyId, title = "Sources", className}: {
  evidence?: readonly unknown[] | null;
  companyId: number;
  title?: string;
  className?: string;
}) {
  const parsed = useMemo(() => dashboardEvidenceItems(evidence), [evidence]);
  if (parsed.length === 0) return null;

  return (
    <EvidenceSourcesPill
      evidence={parsed}
      companyId={companyId}
      title={title}
      compact
      className={className}
    />
  );
}

export function DashboardComponentAccessories({icon, evidence, companyId, info}: {
  icon?: string | null;
  evidence?: readonly unknown[] | null;
  companyId: number;
  info?: ReactNode;
}) {
  if (!icon && !evidence?.length && !info) return null;
  return (
    <>
      {info}
      <DashboardIcon name={icon}/>
      <DashboardEvidence evidence={evidence} companyId={companyId}/>
    </>
  );
}

export function dashboardEvidenceItems(evidence: readonly unknown[] | null | undefined): InsightEvidence[] {
  if (!evidence?.length) return [];
  return parseEvidenceDirectiveGroupJSON({items: evidence})?.evidence ?? [];
}

function dashboardDynamicIconName(name: string | null | undefined): IconName | undefined {
  const normalized = name?.trim();
  if (!normalized) return undefined;
  const kebabName = normalized
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
  const candidates = [
    kebabName,
    kebabName.replace(/([a-z])(\d)/g, "$1-$2"),
    kebabName.replace(/([a-z])(\d)/g, "$1-$2").replace(/(\d)([a-z])/g, "$1-$2"),
  ];
  return candidates.find(candidate => dynamicIconNameSet.has(candidate)) as IconName | undefined;
}
